import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { feedAPI, profileAPI, resolveImageUrl } from '@/lib/api';
import { FeedList } from '@/components/feed/FeedList';
import { ReportModal } from '@/components/feed/ReportModal';
import { TokensSection } from '@/components/TokensSection';
import { ReferralsSection } from '@/components/ReferralsSection';
import { UserBadges } from '@/components/UserBadges';
import { ProfessionalCard } from '@/components/professional/ProfessionalCard';
import { VerifiedBadge } from '@/components/professional/VerifiedBadge';
import { toast } from 'sonner';
import { Calendar, Edit, Lock, Unlock, Coins, FileText, Users as UsersIcon, UserPlus, ShieldCheck, Loader2, Eye, EyeOff } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export function Profile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportingPostId, setReportingPostId] = useState(null);
  const [requestingMentor, setRequestingMentor] = useState(false);

  useEffect(() => {
    loadProfile();
    loadUserPosts();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const response = await profileAPI.getProfile();
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error('Erro ao carregar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestMentor = async () => {
    if (!window.confirm('Deseja solicitar o status de Mentor? Nossa equipe irá analisar sua solicitação.')) {
      return;
    }

    setRequestingMentor(true);
    try {
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${baseUrl}/api/me/request-mentor`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao solicitar status de Mentor');
      }

      toast.success('Solicitação enviada com sucesso! Nossa equipe irá analisar.');
      // Recarregar perfil para atualizar status
      await loadProfile();
    } catch (error) {
      console.error('Failed to request mentor:', error);
      toast.error(error.message || 'Erro ao solicitar status de Mentor');
    } finally {
      setRequestingMentor(false);
    }
  };

  const loadUserPosts = async () => {
    setPostsLoading(true);
    try {
      // Get all posts and filter by current user
      const response = await feedAPI.getPosts();
      const userPosts = response.data.filter(post => post.user_id === user?.id);
      setPosts(userPosts);
    } catch (error) {
      console.error('Failed to load user posts:', error);
    } finally {
      setPostsLoading(false);
    }
  };

  const handleLike = async (postId, shouldLike) => {
    if (shouldLike) {
      await feedAPI.likePost(postId);
    } else {
      await feedAPI.unlikePost(postId);
    }
  };

  const handleComment = async (postId) => {
    console.log('Comment on post:', postId);
  };

  const handleReportOpen = (postId) => {
    setReportingPostId(postId);
    setReportModalOpen(true);
  };

  const handleReportSubmit = async (postId, reason) => {
    await feedAPI.reportPost(postId, reason);
  };

  const handleEdit = async (postId, newContent) => {
    try {
      await feedAPI.editPost(postId, { content: newContent });
      setPosts((prev) => prev.map(p => p.id === postId ? { ...p, content: newContent } : p));
      toast.success('Post editado');
    } catch (err) {
      toast.error('Erro ao editar post');
      throw err;
    }
  };

  const handleDelete = async (postId) => {
    try {
      await feedAPI.deletePost(postId);
      setPosts((prev) => prev.filter(p => p.id !== postId));
      toast.success('Post apagado');
    } catch (err) {
      toast.error('Erro ao apagar post');
      throw err;
    }
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatMemberSince = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale: ptBR,
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card className="mb-6 rounded-2xl border-border/40">
          <CardHeader>
            <div className="flex items-start gap-4">
              <Skeleton className="h-20 w-20 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-7 w-48" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-64" />
              </div>
            </div>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="profile-page">
      {/* Profile Card — Bio Style */}
      <Card className="mb-6 rounded-2xl border-border/40 shadow-sm overflow-hidden">
        {/* Cover gradient */}
        <div className="h-24 bg-gradient-to-r from-violet-500 to-purple-600" />

        <CardHeader className="-mt-12 relative px-6 pb-4">
          <div className="flex items-end gap-4">
            {/* Avatar */}
            <Avatar className="h-20 w-20 ring-4 ring-background" data-testid="profile-avatar">
              <AvatarImage src={resolveImageUrl(profile.avatar_url)} alt={profile.name} />
              <AvatarFallback className="bg-gradient-to-br from-violet-500 to-purple-600 text-white text-xl font-bold">
                {getInitials(profile.name)}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0 pb-1">
              <h1 className="text-xl font-bold" data-testid="profile-name">{profile.name}</h1>
              <VerifiedBadge show={profile.professional_card_verified} testid="profile-verified-badge" />
              <UserBadges user={profile} size="default" />
              <MedalBadge />
            </div>

            <Button
              onClick={() => navigate('/me/edit')}
              variant="outline"
              size="sm"
              className="rounded-xl"
              data-testid="edit-profile-button"
            >
              <Edit className="h-3.5 w-3.5 mr-1.5" />
              Editar
            </Button>
          </div>

          {/* Meta info */}
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <Badge variant="secondary" className="text-xs rounded-md" data-testid="member-since-badge">
              <Calendar className="h-3 w-3 mr-1" />
              Membro {formatMemberSince(profile.created_at)}
            </Badge>
            {profile.verified && (
              <Badge variant="secondary" className="text-xs rounded-md bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                Verificado
              </Badge>
            )}
          </div>

          {/* Bio & Email */}
          <div className="mt-3 space-y-2">
            <p className="text-xs text-muted-foreground" data-testid="profile-email">{profile.email}</p>
            {profile.bio ? (
              <p className="text-sm whitespace-pre-wrap" data-testid="profile-bio">{profile.bio}</p>
            ) : (
              <p className="text-xs text-muted-foreground italic" data-testid="profile-bio-empty">Nenhuma bio adicionada.</p>
            )}
          </div>

          {/* Mentor status */}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {!profile.is_mentor && !profile.mentor_request_pending && (
              <Button onClick={handleRequestMentor} disabled={requestingMentor} variant="outline" size="sm" className="rounded-xl text-xs">
                <UserPlus className="h-3.5 w-3.5 mr-1" />Quero ser Mentor
              </Button>
            )}
            {profile.mentor_request_pending && !profile.is_mentor && (
              <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 text-xs">Solicitação de Mentor Pendente</Badge>
            )}
          </div>

              {/* Clean Days */}
              {(profile.clean_since || (profile.clean_days !== null && profile.clean_days !== undefined)) && (
                <div className="mt-4">
                  <Separator className="mb-4" />
                  <div className="flex items-center gap-2">
                    {profile.clean_days_privacy ? (
                      <Unlock className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Lock className="h-4 w-4 text-muted-foreground" />
                    )}
                    <div>
                      <p className="text-sm font-medium" data-testid="clean-days-count">
                        {profile.clean_days} {profile.clean_days === 1 ? 'dia' : 'dias'} limpo
                      </p>
                      {profile.clean_since && (
                        <p className="text-xs text-green-600" data-testid="clean-since-display">
                          Limpo desde {new Date(profile.clean_since + 'T00:00:00Z').toLocaleDateString('pt-BR')}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {profile.clean_days_privacy
                          ? 'Visível publicamente'
                          : 'Privado (só você vê)'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
        </CardHeader>
      </Card>

      <ProfessionalCard userId={user?.id} editable />

      {/* Tabs for Posts, Tokens, Referrals and Security */}
      <Tabs defaultValue="posts" className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6 rounded-xl">
          <TabsTrigger value="posts" className="flex items-center gap-2 rounded-lg text-xs">
            <FileText className="h-3.5 w-3.5" />
            Posts
          </TabsTrigger>
          <TabsTrigger value="tokens" className="flex items-center gap-2 rounded-lg text-xs">
            <Coins className="h-3.5 w-3.5" />
            Tokens
          </TabsTrigger>
          <TabsTrigger value="referrals" className="flex items-center gap-2 rounded-lg text-xs">
            <UsersIcon className="h-3.5 w-3.5" />
            Indicações
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2 rounded-lg text-xs" data-testid="security-tab">
            <ShieldCheck className="h-3.5 w-3.5" />
            Segurança
          </TabsTrigger>
        </TabsList>

        <TabsContent value="posts">
          {postsLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : posts.length === 0 ? (
            <Card className="p-12 text-center">
              <p className="text-muted-foreground">
                Você ainda não publicou nenhum post. Compartilhe sua jornada no feed!
              </p>
              <Button onClick={() => navigate('/feed')} className="mt-4">
                Ir para o Feed
              </Button>
            </Card>
          ) : (
            <FeedList
              posts={posts}
              loading={false}
              error={null}
              hasMore={false}
              onLoadMore={() => {}}
              onLike={handleLike}
              onComment={handleComment}
              onReport={handleReportOpen}
              onEdit={handleEdit}
              onDelete={handleDelete}
              currentUserId={user?.id}
            />
          )}
        </TabsContent>

        <TabsContent value="tokens">
          <TokensSection />
        </TabsContent>

        <TabsContent value="referrals">
          <ReferralsSection />
        </TabsContent>

        <TabsContent value="security">
          <ChangePasswordSection />
        </TabsContent>
      </Tabs>

      <ReportModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        onSubmit={handleReportSubmit}
        postId={reportingPostId}
      />
    </div>
  );
}


function ChangePasswordSection() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error('Nova senha deve ter no mínimo 6 caracteres'); return; }
    if (newPassword !== confirmPassword) { toast.error('As senhas não coincidem'); return; }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Senha alterada com sucesso!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="rounded-2xl border-border/40 shadow-sm" data-testid="security-section">
      <CardContent className="p-6">
        <h3 className="font-bold text-base mb-1 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-violet-500" /> Alterar Senha
        </h3>
        <p className="text-xs text-muted-foreground mb-5">Sua senha nunca é exibida ou armazenada em texto puro.</p>

        <form onSubmit={handleSubmit} className="space-y-4 max-w-sm">
          <div>
            <Label className="text-sm">Senha atual</Label>
            <div className="relative mt-1">
              <Input
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                placeholder="Sua senha atual"
                data-testid="current-password-input"
              />
              <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-sm">Nova senha</Label>
            <div className="relative mt-1">
              <Input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                placeholder="Mínimo 6 caracteres"
                data-testid="new-password-profile-input"
              />
              <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-sm">Confirmar nova senha</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              placeholder="Repita a nova senha"
              className="mt-1"
              data-testid="confirm-password-profile-input"
            />
          </div>
          <Button type="submit" disabled={saving} className="rounded-xl" data-testid="change-password-submit">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
            Alterar Senha
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}


function MedalBadge() {
  const [medal, setMedal] = useState(null);

  useEffect(() => {
    const fetchMedal = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/api/me/medal-badge`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.medal && res.data.is_public) {
          setMedal(res.data);
        }
      } catch { /* ignore */ }
    };
    fetchMedal();
  }, []);

  if (!medal?.medal) return null;

  return (
    <div className="flex items-center gap-1.5 mt-1" data-testid="medal-badge">
      <img src={medal.medal.image} alt={medal.medal.title_pt} className="w-5 h-5 rounded-full" />
      <span className="text-[10px] font-medium text-amber-400">{medal.medal.title_pt}</span>
      <span className="text-[9px] text-muted-foreground">· {medal.total_days}d</span>
    </div>
  );
}
